// ElevationSidebar.tsx — Phase 4b: elevation list with drag-reorder, split, multi-select
// Left sidebar in Site View showing all project elevations

import { useState } from 'react';
import type { Elevation } from '../types/panelvar';

interface Props {
  elevations: Elevation[];
  selectedId: string | null;
  checkedIds: Set<string>;
  onSelect: (id: string) => void;
  onToggleCheck: (id: string) => void;
  onCheckAll: () => void;
  onUncheckAll: () => void;
  onAdd: (name: string, direction: string) => void;
  onRemove: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onJoin: (ids: string[]) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const DIRECTIONS = ['B', 'F', 'R', 'L'];

export default function ElevationSidebar({ elevations, selectedId, checkedIds, onSelect, onToggleCheck, onCheckAll, onUncheckAll, onAdd, onRemove, onRename, onReorder, onJoin, collapsed, onToggleCollapse }: Props) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDir, setNewDir] = useState('B');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  function handleAdd() {
    if (!newName.trim()) return;
    onAdd(newName.trim(), newDir);
    setNewName('');
    setAdding(false);
  }

  function startRename(e: Elevation) {
    setEditingId(e.id);
    setEditName(e.name);
  }

  function commitRename() {
    if (editingId && editName.trim()) {
      onRename(editingId, editName.trim());
    }
    setEditingId(null);
  }

  // ── Drag handlers ────────────────────────────────
  function handleDragStart(idx: number) {
    setDragIndex(idx);
  }
  function handleDragOver(e: React.DragEvent, idx: number) {
    e.preventDefault();
    setDragOverIndex(idx);
  }
  function handleDrop(idx: number) {
    if (dragIndex !== null && dragIndex !== idx) {
      onReorder(dragIndex, idx);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  }
  function handleDragEnd() {
    setDragIndex(null);
    setDragOverIndex(null);
  }

  // ── Join validation (uses checked elevations) ───
  const checkedElevations = elevations.filter(e => checkedIds.has(e.id));
  const canJoin = checkedElevations.length >= 2 && checkedElevations.every(e => e.direction === checkedElevations[0].direction);

  return (
    <div className="site-view-sidebar">
      <div className="site-sidebar-header">
        {collapsed ? (
          <button className="sidebar-expand-btn" onClick={onToggleCollapse} title="Expand sidebar">▶</button>
        ) : (
          <>
            <h3>Elevations</h3>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button
                className="site-sidebar-add-btn"
                onClick={() => setAdding(!adding)}
                title="Add elevation"
              >+</button>
              <button className="sidebar-collapse-btn" onClick={onToggleCollapse} title="Collapse sidebar">◀</button>
            </div>
          </>
        )}
      </div>

      {collapsed ? null : (
        <>

      <div className="sidebar-select-bar">
        <button className="sidebar-select-btn" onClick={onCheckAll}>All</button>
        <span className="sidebar-select-sep">|</span>
        <button className="sidebar-select-btn" onClick={onUncheckAll}>None</button>
        <span className="sidebar-checked-count">{checkedIds.size}/{elevations.length}</span>
      </div>

      {adding && (
        <div className="site-sidebar-add-form">
          <input
            className="site-sidebar-input"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Name…"
            autoFocus
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <select
            className="site-sidebar-select"
            value={newDir}
            onChange={e => setNewDir(e.target.value)}
          >
            {DIRECTIONS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <button className="site-sidebar-ok-btn" onClick={handleAdd}>Add</button>
        </div>
      )}

      <ul className="site-view-elevation-list">
        {elevations.map((e, idx) => (
          <li key={e.id}>
            {idx > 0 && elevations[idx - 1].direction !== e.direction && (
              <div className="elevation-direction-separator" />
            )}
            <div
              className={
                `site-view-elevation-item`
                + (e.id === selectedId ? ' selected' : '')
                + (dragIndex === idx ? ' dragging' : '')
                + (dragOverIndex === idx && dragIndex !== idx ? ' drag-over' : '')
              }
              onClick={() => onSelect(e.id)}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={ev => handleDragOver(ev, idx)}
              onDrop={() => handleDrop(idx)}
              onDragEnd={handleDragEnd}
            >
              <span className="site-sidebar-drag-handle" title="Drag to reorder">⠿</span>
              <input
                type="checkbox"
                className="sidebar-checkbox"
                checked={checkedIds.has(e.id)}
                onChange={() => onToggleCheck(e.id)}
                onClick={ev => ev.stopPropagation()}
              />
              <span className="site-view-elevation-dir">{e.direction}</span>
              {editingId === e.id ? (
                <input
                  className="site-sidebar-rename-input"
                  value={editName}
                  onChange={ev => setEditName(ev.target.value)}
                  onBlur={commitRename}
                  onKeyDown={ev => { if (ev.key === 'Enter') commitRename(); if (ev.key === 'Escape') setEditingId(null); }}
                  autoFocus
                  onClick={ev => ev.stopPropagation()}
                />
              ) : (
                <span
                  className="site-view-elevation-name"
                  onDoubleClick={() => startRename(e)}
                >{e.name.replace(/^(?:Back|Front|Right|Left)\s+Elevation(?:\s*-\s*)?/, '') || 'Elevation'}</span>
              )}
              <span className="site-view-elevation-count">{e.panelIds.length}</span>
              <button
                className="site-sidebar-remove-btn"
                onClick={ev => { ev.stopPropagation(); onRemove(e.id); }}
                title="Remove elevation"
              >×</button>
            </div>
          </li>
        ))}
      </ul>

      <div className="site-sidebar-summary">
        {elevations.length} elevation{elevations.length !== 1 ? 's' : ''}
      </div>
      {canJoin && (
        <div className="site-sidebar-join-bar">
          <span className="site-sidebar-join-info">
            {checkedElevations.length} checked
          </span>
          <button
            className="site-sidebar-join-btn"
            onClick={() => onJoin(checkedElevations.map(e => e.id))}
            title={`Join ${checkedElevations.length} elevations`}
          >Join</button>
        </div>
      )}
      </>
      )}
    </div>
  );
}
